<script setup lang="ts">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    import { useValidation } from "@/components/forms/reusables/useValidation";
    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSuffix from "@/components/forms/subComponents/Suffix/KSuffix.vue";
    import { useStore } from "@/store";
    import { ResizeObserver } from "@juggle/resize-observer";
    import { useElementVisibility } from "@vueuse/core";
    import debounce from "lodash/debounce";
    import { computed, defineEmits, inject, nextTick, onMounted, ref, toRefs, watch } from "vue";

    const store = useStore();

    // TODO Använd CommonProps i types i Vue3
    interface Props {
        label?: string;
        validation?: string | Array<[rule: string, ...args: never]>;
        type?: "text" | "email" | "password" | "textarea";
        name: string;
        disabled?: boolean;
        readonly?: boolean;
        /**
         * Ska komponenten registreras med formulate vid mount?
         * Används för att undvika renderingsproblem av komponent som kan försvinna helt från DOM
         */
        registerOnMountId?: string;
        /**
         * Ska värdet på fältet tas bort vid submit?
         */
        ignoreValue?: boolean;
        /**
         * Sätt till number om fältet ska visa en counter på antal tecken. Glöm inte att stoppa in max i validation!
         */
        maxCount?: number;
        /**
         * Human readable-namn som ska visas vid error
         */
        validationName?: string;
        /**
         * KSub.vue prop Text som renderas under label
         */
        subText?: string;
        /**
         * KSub.vue prop Ikon som renderas under label
         */
        subIcon?: string;
        /**
         * KSub.vue prop Typ av subtitle
         */
        subType?: string;
        /**
         * KSuffix.vue prop Text som renderas under input
         */
        suffixText?: string;
        /**
         * KSuffix.vue prop Ikon som renderas under input
         */
        suffixIcon?: string;
        /**
         * KSuffix.vue prop Typ av subtitle
         */
        suffixType?: string;
        /**
         * Ska fältet visuellt döljas?
         */
        hidden?: boolean;
        /**
         * Gör så att om värdet i fältet är tomt (tom sträng) eller falsy
         * så kommer det rensas i formulärdatan vid submit.
         */
        ignoreEmptyData?: boolean;
        placeholder?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        type: "text",
        disabled: false,
        ignoreValue: false,
        ignoreEmptyData: false,
    });

    const { name, disabled, type, readonly, registerOnMountId, ignoreValue, maxCount, validation, ignoreEmptyData } =
        toRefs(props);
    const modifiedDisabled = ref(disabled?.value);
    const modifiedReadonly = ref(readonly?.value);
    const inputRef = ref();
    const inputValue = ref();
    const width = ref(0);
    const resizeObserver = ref(
        new ResizeObserver(
            debounce((entries) => {
                const containerElement = entries?.[0]?.target;
                if (
                    type.value === "textarea" &&
                    containerElement &&
                    (containerElement.scrollWidth > width.value || containerElement.scrollWidth < width.value)
                ) {
                    resizeTextarea();
                }
            }, 300)
        )
    );

    const emit = defineEmits<{
        /**
         * ANVÄND BARA DET HÄR NÄR KAUTOCOMPLETE ANVÄNDS UTANFÖR FORMULÄR.
         * Använd KForm listeners/triggers i andra fall
         */
        (event: "inputValue", payload: { value: unknown }): void;
        /**
         * Emittas om användaren klickat på Enter med fokus i input
         */
        (event: "enter"): void;
    }>();

    const count = computed(() =>
        typeof maxCount?.value === "number"
            ? {
                  max: maxCount?.value,
                  current: inputValue?.value?.length ?? 0,
              }
            : undefined
    );

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    } | null>("configNames", null);

    const isDomVisible = useElementVisibility(inputRef);

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: validation?.value,
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    const setDisabled = ({ value }: { value: boolean }) => {
        modifiedDisabled.value = value;
    };

    const setReadonly = ({ value }: { value: boolean }) => {
        modifiedReadonly.value = value;
    };

    defineExpose({
        setDisabled,
        setReadonly,
        ignoreValue: () => ignoreValue.value,
        /*
            Gör så att om värdet i fältet är tomt (tom sträng)
            så kommer det rensas formulärdatan vid submit.
         */
        ignoreEmptyData: () => ignoreEmptyData?.value,
        ...useValidationExpose,
    });

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            ...(configNames ?? {}),
            name: name.value,
        })
    );

    /**
     * Anpassad höjden på textarea efter innehållet, om type === textarea
     */
    const resizeTextarea = () => {
        /*
            Det är i fallen där textarea kan avrenderas som vi vill kolla om det är inom DOM. De har registerOnMountId
            satt för att kunna registera om sig mot formuläret.
         */
        if (registerOnMountId?.value && !isDomVisible?.value) {
            return;
        }

        const textarea = inputRef?.value?.$el?.querySelector("textarea");

        if (!textarea) {
            return;
        }

        // Låter CSS:en automatiskt anpassa höjden för att få plats med all content
        textarea.style.height = "auto";
        // Sätt nya höjden till anpassade höjden + border
        textarea.style.height = `${textarea.scrollHeight + 2}px`;
        textarea.parentElement.style.height = `${textarea.scrollHeight + 2}px`;

        width.value = textarea.scrollWidth + 2;
    };

    /**
     *  Upptäcker modifieddisabled ändras inte när man använder KText utanför KForm, exempelvis i operationsjournalen.
     * */
    watch(disabled, (newVal) => {
        modifiedDisabled.value = newVal;
    });

    onMounted(() => {
        if (registerOnMountId?.value) {
            /*
                Registrerar komponenten till formulate vid mount
                 Används för att undvika renderingsproblem av komponent som kan försvinna helt från DOM
             */
            inputRef.value.$formulate.registry
                .get(registerOnMountId.value)
                .registry.ctx.register(name.value, inputRef.value);
        }

        inputRef.value?.$el && resizeObserver.value.observe(inputRef.value?.$el);

        inputRef.value?.$el?.addEventListener?.("keydown", (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                emit("enter");
            }
        });
    });

    watch(inputValue, async () => {
        await nextTick();
        resizeTextarea();
        emit("inputValue", { value: inputValue.value });
    });

    const onInput = (newValue: string) => {
        inputValue.value = newValue;
    };
</script>
<template>
    <!--
            TODO Ändringar till FormKit:
            outer-class="k-text"
            inner-class="k-text-inner"
            wrapper-class="k-text-wrapper"
            input-class="k-text-input"
            label-class="k-text-label"
        -->
    <FormulateInput
        v-if="shouldBeVisible"
        v-bind="{ ...$attrs, ...$props, ...$slots }"
        ref="inputRef"
        @input="onInput"
        :name="name"
        :type="type"
        :class="['k-text', { textarea: type === 'textarea', hidden, 'readable-disabled': readonly && disabled }]"
        :disabled="modifiedDisabled"
        :readonly="modifiedReadonly"
        rows="1"
        :validationName="validationName ?? label"
        :validation="modifiedValidation"
        :placeholder="placeholder"
    >
        <template #label>
            <KLabel
                v-if="label"
                :validation="modifiedValidation"
                :content="label"
                :subText="subText"
                :subIcon="subIcon"
                :subType="subType"
            />
        </template>
        <template #help>
            <KSuffix
                class="span-all"
                :count="count"
                :subText="suffixText"
                :subIcon="suffixIcon"
                :subType="suffixType"
            />
        </template>
    </FormulateInput>
</template>

<style scoped lang="scss">
    @import "@/style/form";
    @import "@/style/variables";

    .k-text {
        // Utseende för fält som är readonly OCH disabled (för att inte tillåta fokus på fältet)
        &.readable-disabled {
            opacity: 1 !important;
        }

        &.hidden {
            display: none;
        }

        &.textarea {
            // height är satt så att textarea börjar med samma höjd som andra input
            $textarea-height: calc(($form-input-height-default + 2px));

            ::v-deep .formulate-input-element {
                height: $textarea-height;

                textarea {
                    box-sizing: border-box;
                    min-height: $textarea-height;
                    height: $textarea-height;
                    // resize och overflow behövs för autoresize på höjden
                    resize: none;
                    overflow: hidden;
                }
            }
        }

        ::v-deep input {
            height: $form-input-height-default;
        }
    }
</style>
